<template>
  <div class="modal" @click.self.stop="closeModal">
    <section class="modal-content replacement-dialog" role="dialog" aria-modal="true" aria-labelledby="replacement-title" @click.stop>
      <h2 id="replacement-title">Remplacer {{ item.dish.name }} par un autre plat…</h2>
      <fieldset v-if="isBreakfast" class="replacement-scope">
        <legend>Je souhaite changer le petit-déjeuner…</legend>
        <label><input v-model="scope" type="radio" value="day" name="breakfast-scope" @change="refreshScope" /> uniquement pour ce jour</label>
        <label><input v-model="scope" type="radio" value="week" name="breakfast-scope" @change="refreshScope" /> pour toute la semaine affichée</label>
      </fieldset>
      <p class="replacement-explanation">Jusqu’à 5 propositions, en privilégiant l’équilibre de votre semaine. Je peux consulter les ingrédients et la préparation avant de choisir. Les variations d’apports sont signalées.</p>
      <p v-if="errorMessage" role="alert" class="replacement-error">{{ errorMessage }}</p>
      <div class="replacement-modes">
        <button type="button" class="button" :aria-pressed="mode === 'random'" @click="gotoRandomMode">… parmi ces suggestions</button>
        <button type="button" class="button" :aria-pressed="mode === 'liked'" @click="gotoLikedMode">… parmi mes préférés</button>
        <button type="button" class="button" :aria-pressed="mode === 'aliment'" @click="gotoAlimentMode">… contenant un ingrédient</button>
      </div>
      <label v-if="mode === 'aliment'" class="ingredient-search">
        Rechercher un ingrédient
        <input ref="searchInput" class="form-input" placeholder="Par exemple : carotte, cacao…" :value="query" @input="searchForRecipesWithQuery($event.target.value)" />
      </label>
      <LoaderView v-if="loading" />
      <p v-else-if="visibleRecipes && visibleRecipes.length === 0">{{ emptyMessage }}</p>
      <div v-else class="replacement-recipes">
        <article v-for="recipe in visibleRecipes || []" :key="`${scope}-${recipe.id}`" class="replacement-recipe">
          <img v-if="recipe.photo" :src="mediaSrc(recipe.photo)" :alt="recipe.name" loading="lazy" class="replacement-photo" />
          <div class="replacement-recipe-body">
            <h3>{{ recipe.name }}</h3>
            <p v-if="recipe.preparation_time" class="preparation-time">Préparation : {{ recipe.preparation_time }} min</p>
            <p v-if="recipe.replacement_note" class="replacement-note">{{ recipe.replacement_note }}</p>
            <details class="recipe-details">
              <summary>Voir la recette et les ingrédients</summary>
              <h4>Ingrédients</h4>
              <ul>
                <li v-for="(line, index) in recipe.dish_lines || []" :key="index">{{ ingredientLabel(line) }}</li>
              </ul>
              <p class="quantities-note">Les quantités dans mes menus seront adaptées à mon foyer.</p>
              <h4>Préparation</h4>
              <RecipePreparation :steps="recipe.preparation_steps || []" :text="recipe.recipe || ''" />
            </details>
            <button type="button" class="button" @click="selectedRecipe = recipe">Choisir ce plat</button>
          </div>
        </article>
      </div>
      <div class="replacement-actions"><button type="button" class="button gray" @click="closeModal">Fermer</button></div>
    </section>
    <div v-if="selectedRecipe" class="modal confirmation-overlay" @click.self.stop="selectedRecipe = null">
      <section class="modal-content" role="dialog" aria-modal="true" aria-labelledby="replacement-confirm-title">
        <h2 id="replacement-confirm-title">Confirmer le remplacement</h2>
        <p>Je remplace <strong>{{ item.dish.name }}</strong> par <strong>{{ selectedRecipe.name }}</strong>.</p>
        <p v-if="isBreakfast" class="scope-confirmation"><strong>{{ scope === 'week' ? 'Pour tous les petits-déjeuners de la semaine affichée.' : 'Uniquement pour ce jour.' }}</strong></p>
        <p v-if="selectedRecipe.replacement_note">{{ selectedRecipe.replacement_note }}</p>
        <div class="replacement-actions">
          <button type="button" class="button gray" :disabled="replacing" @click="selectedRecipe = null">Retour aux propositions</button>
          <button type="button" class="button" :disabled="replacing" @click="replace">{{ replacing ? 'Remplacement…' : 'Confirmer le remplacement' }}</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import LoaderView from "@/components/LoaderView.vue";
import RecipePreparation from "@/components/RecipePreparation.vue";
import { api } from "@/api";
import { userState } from "@/utils/store";

export default {
  name: "ReplaceDishModal",
  components: { LoaderView, RecipePreparation },
  props: { item: { type: Object, required: true }, date: String },
  data() {
    return { mode: "random", scope: "day", replacing: false, loading: false, errorMessage: "", randomRecipes: null, likedRecipes: null, recipesWithAliment: null, query: "", selectedRecipe: null, searchTimeout: null, requestRevision: 0 };
  },
  computed: {
    isBreakfast() { return this.item.dish_type === "breakfast" || this.item.dish.dish_type === "breakfast"; },
    visibleRecipes() { return this.mode === "liked" ? this.likedRecipes : this.mode === "aliment" ? this.recipesWithAliment : this.randomRecipes; },
    emptyMessage() {
      if (this.mode === "liked") return "Aucun de mes plats préférés ne convient à ce remplacement. Je peux essayer les autres suggestions.";
      if (this.mode === "aliment") return "Aucune proposition avec cet ingrédient pour cette période. Je peux essayer un autre ingrédient.";
      return "Aucune alternative compatible pour cette période. Je peux essayer une autre période ou un autre mode de recherche.";
    },
  },
  mounted() { this.fetchSuggestions(); },
  beforeDestroy() { clearTimeout(this.searchTimeout); this.requestRevision += 1; },
  methods: {
    closeModal() { if (!this.replacing) this.$emit("close"); },
    gotoRandomMode() { this.mode = "random"; this.fetchSuggestions(); },
    gotoLikedMode() { this.mode = "liked"; this.fetchSuggestions(); },
    gotoAlimentMode() { this.mode = "aliment"; this.fetchSuggestions(); this.$nextTick(() => this.$refs.searchInput?.focus()); },
    refreshScope() {
      this.selectedRecipe = null;
      this.randomRecipes = this.likedRecipes = this.recipesWithAliment = null;
      this.fetchSuggestions();
    },
    searchForRecipesWithQuery(query) {
      this.query = query;
      clearTimeout(this.searchTimeout);
      this.requestRevision += 1;
      if (!query.trim()) { this.recipesWithAliment = null; this.loading = false; return; }
      this.loading = true;
      this.searchTimeout = setTimeout(() => this.fetchSuggestions(), 250);
    },
    async fetchSuggestions() {
      clearTimeout(this.searchTimeout);
      const revision = ++this.requestRevision;
      const mode = this.mode;
      const scope = this.scope;
      this.errorMessage = "";
      if (mode === "aliment" && !this.query.trim()) { this.loading = false; return; }
      this.loading = true;
      try {
        const res = await api.get("replacement-suggestions/", { params: {
          item_id: this.item.id, dish_id: this.item.dish.id, meal: this.item.meal,
          date: this.date, type: this.item.dish.dish_type, scope: this.isBreakfast ? scope : "day",
          ...(mode === "liked" ? { only_favorites: true } : {}),
          ...(mode === "aliment" ? { q: this.query.trim() } : {}),
        } });
        if (revision !== this.requestRevision) return;
        const recipes = res.data.slice(0, 5);
        if (mode === "liked") this.likedRecipes = recipes;
        else if (mode === "aliment") this.recipesWithAliment = recipes;
        else this.randomRecipes = recipes;
      } catch (error) {
        if (revision === this.requestRevision) this.errorMessage = "Impossible de charger les propositions. Réessayez.";
      } finally {
        if (revision === this.requestRevision) this.loading = false;
      }
    },
    ingredientLabel(line) {
      const name = line.aliment?.display_name || line.aliment?.name || "Ingrédient";
      const quantity = line.quantity == null ? "" : String(line.quantity).replace(".", ",");
      const units = { piece: "pièce(s)", pincee: "pincée(s)", cuillere_a_soupe: "cuillère(s) à soupe", cuillere_a_cafe: "cuillère(s) à café" };
      return [quantity, units[line.unit] || line.unit || "", "—", name].filter(Boolean).join(" ");
    },
    mediaSrc(uri) { return /^https?:\/\//.test(uri || "") ? uri : `${process.env.VUE_APP_BASE_URL}${uri}`; },
    async replace() {
      if (this.replacing || !this.selectedRecipe) return;
      this.replacing = true;
      this.errorMessage = "";
      try {
        const res = await api.post(`/replace-my-item/${this.item.id}/`, { recipe_id: this.selectedRecipe.id, scope: this.isBreakfast ? this.scope : "day" });
        userState.menuRevision = (userState.menuRevision || 0) + 1;
        this.$emit("update", res.data);
        this.$emit("close");
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || "Le remplacement a échoué. Réessayez.";
        this.selectedRecipe = null;
      } finally { this.replacing = false; }
    },
  },
};
</script>

<style scoped>
.replacement-dialog { width: min(760px, 95vw); }
.modal-content { max-height: 90vh; overflow-y: auto; }
h2 { font-size: 21px; }
.replacement-scope { border: 1px solid #b3c798; padding: 12px 15px; border-radius: 12px; margin: 15px 0; }
.replacement-scope legend { font-size: 16px; font-weight: 700; float: none; width: auto; }
.replacement-scope label { display: flex; align-items: center; gap: 9px; margin: 8px 0; }
.replacement-modes, .replacement-actions { display: flex; flex-wrap: wrap; gap: 10px; margin: 16px 0; }
.replacement-actions { justify-content: flex-end; }
.replacement-explanation, .preparation-time, .quantities-note { font-size: 14px; color: #50613d; }
.replacement-error { color: #a12323; }
.replacement-recipes { display: flex; flex-direction: column; gap: 18px; }
.replacement-recipe { border: 1px solid #d8e3cb; border-radius: 12px; overflow: hidden; background: #fff; }
.replacement-photo { width: 100%; height: 145px; object-fit: cover; }
.replacement-recipe-body { padding: 15px; }
h3 { font-size: 19px; margin: 0 0 6px; }
h4 { font-size: 16px; margin-top: 15px; }
.replacement-note { color: #725716; background: #fff8df; padding: 9px; border-radius: 7px; font-size: 14px; }
.recipe-details { margin: 12px 0; }
.recipe-details summary { color: #436f0e; font-weight: 700; cursor: pointer; padding: 8px 0; }
.recipe-details li { margin-bottom: 5px; }
.ingredient-search { display: block; margin-bottom: 15px; }
.scope-confirmation { color: #436f0e; }
.confirmation-overlay { z-index: 1300; }
</style>
